import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { SWEDISH_LOCALES } from '@packages/locale/constants/lang.ts'
import { CHARTER_MARKETS } from '@packages/locale/constants/locales.ts'
import {
  LOCALES,
  LOCALES_WITH_LOGIN,
  LOCALES_WITH_FAVOURITES,
  LOCALES_WITH_CHARTER_PACKAGES,
  LOCALES_WITH_AIRPORT_SPECIFIC_NEWSLETTER,
} from '@packages/locale/constants/locales.ts'
import type { Market } from '@packages/locale/types/locales.ts'
import { Markets } from '@packages/locale/constants/locales.ts'

import { mergePageWideTranslations } from '@layers/web/utils/cms.ts'
import type {
  LocaleStoreState,
} from '@layers/web/stores/types/locale.ts'
import { CACHE_KEYS } from '@layers/web/constants/cache.ts'
import type { FetchTranslationKeyParams } from '@layers/web/stores/types/locale.ts'
import { LocaleApiService } from '@layers/web/services/locale-api.ts'

const useLocaleStore = defineStore('locale', {
  state: (): LocaleStoreState => {
    const { public: { DEFAULT_LOCALE, localeURLs, disableLogin, disableCharter } } = useRuntimeConfig()

    return {
      locale: DEFAULT_LOCALE,
      urls: { ...(localeURLs as Record<string, string> || {}) },
      locales: LOCALES,
      localeFallback: DEFAULT_LOCALE,
      translationToolsActive: false,
      pageWideTranslationBuckets: {},
      charterMarkets: CHARTER_MARKETS,
      loginDisabled: disableLogin,
      charterDisabled: disableCharter,
    }
  },

  getters: {
    getTemporaryFallbackLocale (state): string {
      return (this as { $isSolWebsite?: boolean }).$isSolWebsite ? 'sv' : state.locale
    },

    getLocaleLanguage (state): string {
      return localeToLang(state.locale)
    },

    getLocaleLanguageCode (state): string {
      return localeToLangInfo(state.locale).code
    },

    localeURLs (state) {
      return state.urls
    },

    isRolfSwe (state): boolean {
      return state.locale === Markets.SWEDISH
    },

    washExternalLinkToInternal: (state) => (url: string, full = false): string => {
      const root = state.urls?.BASE_URL
      return url.replace(`${root}${full ? '/' : ''}`, '')
    },

    getLocale (state): string {
      return state.locale
    },

    isSwedish (state): boolean {
      return SWEDISH_LOCALES.includes(state.locale)
    },

    canLocaleLogin (state): boolean {
      return !state.loginDisabled && LOCALES_WITH_LOGIN.includes(state.locale)
    },

    canLocaleFavorite (state): boolean {
      return LOCALES_WITH_FAVOURITES.includes(state.locale)
    },

    canLocaleBookCharter (state): boolean {
      return !state.charterDisabled && LOCALES_WITH_CHARTER_PACKAGES.includes(state.locale)
    },

    hasLocaleAirportSpecificNewsletter: (state): boolean => LOCALES_WITH_AIRPORT_SPECIFIC_NEWSLETTER.includes(state.locale),
  },

  actions: {
    setLocale (locale: Market): void {
      this.locale = locale
    },

    setTranslationTools (active: boolean): void {
      this.translationToolsActive = active
    },

    addPageWideTranslation (key: string, value: PageWideTranslation): void {
      const existing = this.pageWideTranslationBuckets[key]
      this.pageWideTranslationBuckets[key] = mergePageWideTranslations(
        existing ? [existing, value] : [value]
      )
    },

    async fetchTranslationKey ({ key, type }: FetchTranslationKeyParams) {
      try {
        const { data: result } = await apiFetch(`/${this.locale}/${type}/original/${key}`)

        if (result) {
          return result as string
        }
        return null
      } catch (e: unknown) {
        Sentry.setContext('translation', {
          'translation_key': key,
          'error': JSON.stringify(e),
        })
        Sentry.captureMessage('Error fetching translation', {
          level: 'error',
          tags: { type: 'translation' },
        })
        return null
      }
    },

    async updateTranslation ({ type, payload }: { type: string; payload: any }): Promise<any> {
      try {
        const result = await this.submitTranslationUpdate(type, payload)
        if (!result) {
          return null
        }

        if (payload.field === 'locale') {
          await this.refreshTranslations()
        }

        return result
      } catch (e) {
        console.error('Failed updating translation: ', payload, e)
        return null
      }
    },

    async submitTranslationUpdate (type: string, payload: any): Promise<any> {
      const { data: result } = await apiFetch(`/${this.locale}/${type}/update`, {
        method: 'POST',
        body: payload,
      })

      return result
    },

    async refreshTranslations (): Promise<void> {
      await this.clearTranslationCache()

      const updatedTranslations = await LocaleApiService.fetchRuntimeTranslations({ cacheReload: true })

      if (!updatedTranslations) {
        return
      }
      this.mergeTranslations(updatedTranslations)
    },

    async clearTranslationCache (): Promise<void> {
      await $fetch(`/localapi/cache-clear/${CACHE_KEYS.TRANSLATIONS}`)
    },

    mergeTranslations (translations: Record<string, any>): void {
      const { $i18n } = useNuxtApp()
      // @ts-expect-error - mergeTranslations exists on $i18n from nuxt-i18n-micro
      $i18n?.$mergeTranslations(translations)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLocaleStore, import.meta.hot))
}

export { useLocaleStore }